import { ToggleStateStatus, ToggleStateValue, } from "./StateReducer.types";
var DOMBuilder = /** @class */ (function () {
    /**
     * Initializes a new instance of the DOMBuilder class.
     * @param checkbox HTMLInputElement element representing the toggle.
     * @param options ToggleOptions object containing options for the toggle.
     * @param state ToggleState object containing the initial state of the toggle.
     */
    function DOMBuilder(checkbox, options, state) {
        this.onStyle = "btn-".concat(options.onstyle);
        this.offStyle = "btn-".concat(options.offstyle);
        this.name = options.name;
        this.checkbox = checkbox;
        if (options.onvalue)
            this.checkbox.value = options.onvalue;
        this.invCheckbox = options.offvalue
            ? this.createInvCheckbox(options.offvalue)
            : null;
        this.sizeClass = DOMBuilder.sizeResolver(options.size);
        this.toggleOn = this.createToggleSpan(options.onlabel, this.onStyle, options.ontitle);
        this.toggleOff = this.createToggleSpan(options.offlabel, this.offStyle, options.offtitle);
        this.toggleHandle = this.createToggleHandle();
        this.toggleGroup = this.createToggleGroup();
        this.toggle = document.createElement("div");
        this.renderToggle(options);
        this.render(state);
    }
    /**
     * Resolves the size class for the toggle based on the provided size.
     * If size is not provided or is invalid, returns an empty string.
     * @param size ToggleSize value representing the size of the toggle.
     * @returns string representing the size class for the toggle.
     */
    DOMBuilder.sizeResolver = function (size) {
        var _a;
        var sizeMap = {
            large: "btn-lg",
            lg: "btn-lg",
            small: "btn-sm",
            sm: "btn-sm",
            mini: "btn-xs",
            xs: "btn-xs",
        };
        return (_a = sizeMap[size]) !== null && _a !== void 0 ? _a : "";
    };
    /**
     * Creates an inverted checkbox element that is used in the toggle.
     * This checkbox is used to create the toggle's "off" state.
     * @param offValue The value of the checkbox when the toggle is in the "off" state.
     * @returns An HTMLInputElement representing the inverted checkbox element.
     */
    DOMBuilder.prototype.createInvCheckbox = function (offValue) {
        var invCheckbox = this.checkbox.cloneNode(true);
        invCheckbox.value = offValue;
        invCheckbox.setAttribute("data-toggle", "invert-toggle");
        invCheckbox.removeAttribute("id");
        return invCheckbox;
    };
    /**
     * Renders the toggle element and its children.
     * Sets the class attribute of the toggle with the provided style and size class.
     * Sets the tabindex attribute of the toggle with the provided tabindex.
     * Inserts the toggle element before the original checkbox element.
     * Appends the checkbox, inverted checkbox (if exists) and toggle group elements to the toggle element.
     * Handles the toggle size by setting the width and height attributes of the toggle element.
     * @param options - ToggleOptions object containing the style, width, height and tabindex for the toggle.
     */
    DOMBuilder.prototype.renderToggle = function (_a) {
        var _b;
        var style = _a.style, width = _a.width, height = _a.height, tabindex = _a.tabindex;
        this.toggle.setAttribute("class", "toggle btn ".concat(this.sizeClass, " ").concat(style));
        this.toggle.setAttribute("data-toggle", "toggle");
        this.toggle.tabIndex = tabindex;
        this.toggle.role = "button";
        (_b = this.checkbox.parentElement) === null || _b === void 0 ? void 0 : _b.insertBefore(this.toggle, this.checkbox);
        this.toggle.appendChild(this.checkbox);
        if (this.invCheckbox)
            this.toggle.appendChild(this.invCheckbox);
        this.toggle.appendChild(this.toggleGroup);
        this.handleToggleSize(width, height);
    };
    /**
     * Creates a div element representing the toggle group.
     * The toggle group contains the on, off, and handle elements of the toggle.
     * @returns An HTMLElement representing the toggle group element.
     */
    DOMBuilder.prototype.createToggleGroup = function () {
        var toggleGroup = document.createElement("div");
        toggleGroup.setAttribute("class", "toggle-group");
        toggleGroup.appendChild(this.toggleOn);
        toggleGroup.appendChild(this.toggleOff);
        toggleGroup.appendChild(this.toggleHandle);
        return toggleGroup;
    };
    /**
     * Creates a span element representing a toggle option (on/off).
     * The span element is given a class attribute with the provided style and size class.
     * The innerHTML of the span element is set to the provided label.
     * If a title is provided, the span element is given a title attribute with the provided title.
     * @param label The text to be displayed in the toggle option.
     * @param style The style of the toggle option (primary, secondary, etc.).
     * @param title The title of the toggle option.
     * @returns An HTMLElement representing the toggle option element.
     */
    DOMBuilder.prototype.createToggleSpan = function (label, style, title) {
        var toggleSpan = document.createElement("span");
        toggleSpan.setAttribute("class", "btn ".concat(this.sizeClass, " ").concat(style));
        toggleSpan.innerHTML = label;
        if (title)
            toggleSpan.setAttribute("title", title);
        return toggleSpan;
    };
    /**
     * Creates a span element representing the toggle handle.
     * The span element is given a class attribute with the provided size class.
     * @returns An HTMLElement representing the toggle handle element.
     */
    DOMBuilder.prototype.createToggleHandle = function () {
        var toggleHandle = document.createElement("span");
        toggleHandle.setAttribute("class", "toggle-handle btn ".concat(this.sizeClass));
        return toggleHandle;
    };
    /**
     * Sets the width and height of the toggle element.
     * If a width or height is not provided, the toggle element will be given a minimum width and height
     * that is calculated based on the size of the toggle on and off options.
     * @param width The width of the toggle element.
     * @param height The height of the toggle element.
     */
    DOMBuilder.prototype.handleToggleSize = function (width, height) {
        function calcH(toggleSpan) {
            var styles = window.getComputedStyle(toggleSpan);
            var height = toggleSpan.offsetHeight;
            var borderTopWidth = parseFloat(styles.borderTopWidth);
            var borderBottomWidth = parseFloat(styles.borderBottomWidth);
            var paddingTop = parseFloat(styles.paddingTop);
            var paddingBottom = parseFloat(styles.paddingBottom);
            return (height - borderBottomWidth - borderTopWidth - paddingTop - paddingBottom);
        }
        if (width) {
            this.toggle.style.width = width;
        }
        else {
            this.toggle.style.minWidth = "100px"; // First approach for better calculation
            this.toggle.style.minWidth = "".concat(Math.max(this.toggleOn.getBoundingClientRect().width, this.toggleOff.getBoundingClientRect().width) +
                this.toggleHandle.getBoundingClientRect().width / 2, "px");
        }
        if (height) {
            this.toggle.style.height = height;
        }
        else {
            this.toggle.style.minHeight = "36px"; // First approach for better calculation
            this.toggle.style.minHeight = "".concat(Math.max(this.toggleOn.getBoundingClientRect().height, this.toggleOff.getBoundingClientRect().height), "px");
        }
        // B: Apply on/off class
        this.toggleOn.classList.add("toggle-on");
        this.toggleOff.classList.add("toggle-off");
        // C: Finally, set lineHeight if needed
        if (height) {
            this.toggleOn.style.lineHeight = calcH(this.toggleOn) + "px";
            this.toggleOff.style.lineHeight = calcH(this.toggleOff) + "px";
        }
    };
    /**
     * Renders the toggle element based on the provided state.
     * @param {ToggleState} state The state of the toggle element.
     */
    DOMBuilder.prototype.render = function (state) {
        this.toggle.classList.remove(this.onStyle, this.offStyle, "off", "indeterminate");
        switch (state.value) {
            case ToggleStateValue.ON:
                this.toggle.classList.add(this.onStyle);
                break;
            case ToggleStateValue.OFF:
                this.toggle.classList.add(this.offStyle, "off");
                break;
            case ToggleStateValue.INDETERMINATE:
                this.toggle.classList.add("indeterminate");
                if (state.checked) {
                    this.toggle.classList.add(this.onStyle);
                }
                else {
                    this.toggle.classList.add(this.offStyle, "off");
                }
                break;
        }
        this.checkbox.checked = state.checked;
        if (this.invCheckbox)
            this.invCheckbox.checked = !state.checked;
        this.toggle.classList.add(state.checked ? this.onStyle : this.offStyle);
        switch (state.status) {
            case ToggleStateStatus.ENABLED:
                this.toggle.classList.remove("disabled");
                this.toggle.removeAttribute("disabled");
                this.checkbox.disabled = false;
                this.checkbox.readOnly = false;
                if (this.invCheckbox) {
                    this.invCheckbox.disabled = false;
                    this.invCheckbox.readOnly = false;
                }
                break;
            case ToggleStateStatus.DISABLED:
                this.toggle.classList.add("disabled");
                this.toggle.setAttribute("disabled", "");
                this.checkbox.disabled = true;
                this.checkbox.readOnly = false;
                if (this.invCheckbox) {
                    this.invCheckbox.disabled = true;
                    this.invCheckbox.readOnly = false;
                }
                break;
            case ToggleStateStatus.READONLY:
                this.toggle.classList.add("disabled");
                this.toggle.setAttribute("disabled", "");
                this.checkbox.disabled = false;
                this.checkbox.readOnly = true;
                if (this.invCheckbox) {
                    this.invCheckbox.disabled = false;
                    this.invCheckbox.readOnly = true;
                }
                break;
        }
        if (state.indeterminate) {
            this.checkbox.indeterminate = true;
            this.checkbox.removeAttribute("name");
            if (this.invCheckbox)
                this.invCheckbox.indeterminate = true;
            if (this.invCheckbox)
                this.invCheckbox.removeAttribute("name");
        }
        else {
            this.checkbox.indeterminate = false;
            if (this.name)
                this.checkbox.name = this.name;
            if (this.invCheckbox)
                this.invCheckbox.indeterminate = false;
            if (this.invCheckbox && this.name)
                this.invCheckbox.name = this.name;
        }
    };
    Object.defineProperty(DOMBuilder.prototype, "root", {
        /**
         * Returns the root element of the toggle, which is the container of all toggle elements.
         * @returns {HTMLElement} The root element of the toggle.
         */
        get: function () {
            return this.toggle;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Destroys the toggle by removing the toggle element from the DOM and
     *inserting the original checkbox element back into its original position.
     */
    DOMBuilder.prototype.destroy = function () {
        var _a, _b;
        (_a = this.toggle.parentNode) === null || _a === void 0 ? void 0 : _a.insertBefore(this.checkbox, this.toggle);
        (_b = this.toggle.parentNode) === null || _b === void 0 ? void 0 : _b.removeChild(this.toggle);
    };
    return DOMBuilder;
}());
export { DOMBuilder };
